import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, Row, Col, Card, Table, Form,
  Button, Spinner, Alert,
} from 'react-bootstrap';
import Layout from '../components/Layout';
import qnaService from '../services/qnaService';
import { useAuth } from '../context/AuthContext';

const STATUS_OPTIONS = [
  { value: '',         label: 'جديد',          variant: 'secondary' },
  { value: 'pending',  label: 'قيد الانتظار',  variant: 'warning'   },
  { value: 'answered', label: 'تم الرد',        variant: 'success'   },
  { value: 'trash',    label: 'مهمل',           variant: 'danger'    },
];

const breadcrumbs = [
  { label: 'الرئيسية', path: '/dashboard', icon: 'bi-house' },
  { label: 'الأسئلة والأجوبة', icon: 'bi-chat-dots' },
];

const buildAnswerWhatsAppUrl = (q) => {
  if (!q.whatsapp) return null;
  const R = '‏';
  const answer = q.answer || '';
  if (!answer.trim()) return null;
  const msg =
    `${R}السلام عليكم و رحمة الله و بركاته\n` +
    `${R}لقد سألتم في موقع قناة مسجد الموسوي الكبير\n\n` +
    `${R}*رقم التذكرة:*\t${q.ticketNumber || '-'}\n` +
    `${R}*الاسم:*\t\t${q.name || '-'}\n` +
    `${R}*الكتاب:*\t\t${q.book || '-'}\n` +
    `${R}*واتساب:*\t\t${q.whatsapp || '-'}\n` +
    `${R}*البريد:*\t\t${q.email || '-'}\n` +
    `${R}*المنصة:*\t\t${q.platform || '-'}\n\n` +
    `${R}*السؤال:*\n${q.question}\n\n` +
    `${R}*الجواب:*\n${answer}`;
  return `https://wa.me/${q.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(msg)}`;
};

const buildWhatsAppUrl = (q, sheikhs) => {
  const sh = sheikhs.find(s => s.name === q.sheikh);
  if (!sh || !sh.whatsapp) return null;
  const R = '‏';
  const msg =
    `${R}*رقم التذكرة:* ${q.ticketNumber || '-'}\n` +
    `${R}*الاسم:*\t\t${q.name}\n` +
    `${R}*الكتاب:*\t\t${q.book || '-'}\n` +
    `${R}*واتساب:*\t\t${q.whatsapp || '-'}\n` +
    `${R}*البريد:*\t\t${q.email || '-'}\n` +
    `${R}*المنصة:*\t\t${q.platform || '-'}\n` +
    `${R}*السؤال:*\n${q.question}`;
  return `https://wa.me/${sh.whatsapp}?text=${encodeURIComponent(msg)}`;
};

const fmtDate = (ts) => {
  if (!ts) return '-';
  try {
    return new Date(ts).toLocaleString('ar-IQ', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return ts;
  }
};

const InfoRow = ({ icon, label, value }) => (
  <div className="d-flex align-items-start gap-2 mb-2">
    <i className={`${icon} text-secondary mt-1`} style={{ flexShrink: 0, fontSize: '0.9rem' }} />
    <div>
      <div className="text-muted" style={{ fontSize: '0.72rem' }}>{label}</div>
      <div style={{ fontSize: '0.88rem', wordBreak: 'break-all' }}>{value || '-'}</div>
    </div>
  </div>
);

const StatCard = ({ icon, label, value, color }) => (
  <Card className="border-0 shadow-sm h-100">
    <Card.Body className="d-flex align-items-center gap-3 p-3">
      <div style={{
        width: 54, height: 54, borderRadius: '50%',
        background: color + '22', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <i className={icon} style={{ fontSize: '1.4rem', color }} />
      </div>
      <div>
        <div style={{ fontSize: '1.75rem', fontWeight: 700, lineHeight: 1 }}>{value}</div>
        <div className="text-muted small mt-1">{label}</div>
      </div>
    </Card.Body>
  </Card>
);

export default function QNA() {
  const { canEdit, isAdmin, isAuditor, isSheikh, user } = useAuth();
  const [data, setData] = useState({ questions: [], sheikhs: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSheikh, setFilterSheikh] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [draftAnswer, setDraftAnswer] = useState({});
  const [draftANotes, setDraftANotes] = useState({});
  const [saving, setSaving] = useState({});
  const [saveResult, setSaveResult] = useState({});
  const [emailSending, setEmailSending] = useState({});
  const [emailResult, setEmailResult] = useState({});

  const sheikhRecord = useMemo(
    () => isSheikh ? data.sheikhs.find(s => s.email === user?.email) : null,
    [isSheikh, data.sheikhs, user?.email],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await qnaService.getAll();
      setData(result);
    } catch (e) {
      setError('تعذّر تحميل البيانات: ' + (e.message || 'خطأ غير معروف'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return data.questions.filter(q => {
      if (isSheikh && q.sheikh !== sheikhRecord?.name) return false;
      const matchSearch = !s || [q.name, q.question, q.book, String(q.ticketNumber || '')]
        .some(v => v?.toLowerCase().includes(s));
      const matchStatus = filterStatus === 'all' || (q.status || '') === filterStatus;
      const matchSheikh = !filterSheikh || q.sheikh === filterSheikh;
      return matchSearch && matchStatus && matchSheikh;
    });
  }, [data.questions, search, filterStatus, filterSheikh, isSheikh, sheikhRecord]);

  const sheikhQuestions = useMemo(
    () => isSheikh
      ? data.questions.filter(q => q.sheikh === sheikhRecord?.name)
      : data.questions,
    [data.questions, isSheikh, sheikhRecord],
  );

  const stats = useMemo(() => ({
    total: sheikhQuestions.length,
    answered: sheikhQuestions.filter(q => q.status === 'answered').length,
    pending: sheikhQuestions.filter(q => (q.status || '') !== 'answered').length,
    unassigned: data.questions.filter(q => !q.sheikh).length,
  }), [sheikhQuestions, data.questions]);

  const canWriteAnswer = canEdit || isSheikh;

  const setQuestion = (id, patch) =>
    setData(d => ({
      ...d,
      questions: d.questions.map(q => q.id === id ? { ...q, ...patch } : q),
    }));

  const doSave = async (key, apiFn, onSuccess) => {
    setSaving(s => ({ ...s, [key]: true }));
    setSaveResult(r => ({ ...r, [key]: null }));
    try {
      await apiFn();
      onSuccess?.();
      setSaveResult(r => ({ ...r, [key]: 'ok' }));
      setTimeout(() => setSaveResult(r => ({ ...r, [key]: null })), 2500);
    } catch (e) {
      setSaveResult(r => ({ ...r, [key]: 'err' }));
    } finally {
      setSaving(s => ({ ...s, [key]: false }));
    }
  };

  const handleSheikh = (id, sheikh) => {
    const question = data.questions.find(q => q.id === id);
    const assignedSheikh = data.sheikhs.find(s => s.name === sheikh);
    doSave(
      `sh_${id}`,
      () => qnaService.updateSheikh(id, sheikh),
      () => {
        setQuestion(id, { sheikh });
        if (sheikh && assignedSheikh) {
          qnaService.notifySheikh({
            sheikhName:   assignedSheikh.name,
            sheikhEmail:  assignedSheikh.email || '',
            ticketNumber: question?.ticketNumber || '',
            questionId:   String(id),
          }).catch(() => {});
        }
      },
    );
  };

  const handleStatus = (id, status) =>
    doSave(
      `st_${id}`,
      () => qnaService.updateStatus(id, status),
      () => setQuestion(id, { status }),
    );

  const handleAnswer = (id) => {
    const answer = draftAnswer[id];
    if (answer == null) return;
    doSave(
      `an_${id}`,
      () => qnaService.updateAnswer(id, answer),
      () => {
        setQuestion(id, { answer });
        setDraftAnswer(d => { const n = { ...d }; delete n[id]; return n; });
      },
    );
  };

  const handleANotes = (id) => {
    const notes = draftANotes[id];
    if (notes == null) return;
    doSave(
      `anotes_${id}`,
      () => qnaService.updateNotes(id, notes),
      () => {
        setQuestion(id, { anotes: notes });
        setDraftANotes(d => { const n = { ...d }; delete n[id]; return n; });
      },
    );
  };

  const handleApprove = (id) =>
    doSave(
      `approve_${id}`,
      () => qnaService.updateStatus(id, 'audited'),
      () => setQuestion(id, { status: 'audited' }),
    );

  const handleSendEmail = async (q) => {
    const key = `email_${q.id}`;
    setEmailSending(s => ({ ...s, [key]: true }));
    setEmailResult(r => ({ ...r, [key]: null }));
    try {
      await qnaService.sendEmail(q);
      setEmailResult(r => ({ ...r, [key]: 'ok' }));
      setTimeout(() => setEmailResult(r => ({ ...r, [key]: null })), 3000);
    } catch {
      setEmailResult(r => ({ ...r, [key]: 'err' }));
    } finally {
      setEmailSending(s => ({ ...s, [key]: false }));
    }
  };

  const isSaving = (k) => !!saving[k];
  const result = (k) => saveResult[k];

  return (
    <Layout title="الأسئلة والأجوبة" breadcrumbs={breadcrumbs}>
      <Container fluid dir="rtl">

        {/* Sheikh identity banner */}
        {isSheikh && (
          <div
            className="d-flex align-items-center gap-2 mb-4 p-3 rounded-3"
            style={{ background: 'rgba(13,110,253,0.07)', border: '1px solid rgba(13,110,253,0.2)' }}
          >
            <i className="bi bi-person-badge-fill text-primary fs-5" />
            <div>
              <span className="fw-semibold">
                {sheikhRecord ? sheikhRecord.name : 'الشيخ'}
              </span>
              {!sheikhRecord && (
                <span className="text-danger small me-2">
                  — لم يُعثر على سجل شيخ مرتبط بهذا البريد الإلكتروني
                </span>
              )}
            </div>
          </div>
        )}

        {/* Stats */}
        <Row className="mb-4 g-3">
          <Col md={isSheikh ? 4 : 3} sm={6}>
            <StatCard icon="bi-chat-dots-fill" label={isSheikh ? 'أسئلتي' : 'إجمالي الأسئلة'} value={stats.total} color="#0d6efd" />
          </Col>
          <Col md={isSheikh ? 4 : 3} sm={6}>
            <StatCard icon="bi-check-circle-fill" label="تم الرد" value={stats.answered} color="#198754" />
          </Col>
          <Col md={isSheikh ? 4 : 3} sm={6}>
            <StatCard icon="bi-clock-fill" label="بانتظار الرد" value={stats.pending} color="#ffc107" />
          </Col>
          {!isSheikh && (
            <Col md={3} sm={6}>
              <StatCard icon="bi-person-x-fill" label="بدون شيخ" value={stats.unassigned} color="#dc3545" />
            </Col>
          )}
        </Row>

        {/* Filters */}
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body>
            <Row className="g-2 align-items-end">
              <Col xs={12} md={5}>
                <Form.Control
                  placeholder="بحث بالاسم، الكتاب، السؤال، رقم التذكرة..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </Col>
              <Col xs={6} md={2}>
                <Form.Select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                  <option value="all">كل الحالات</option>
                  {STATUS_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Form.Select>
              </Col>
              {!isSheikh && (
                <Col xs={6} md={3}>
                  <Form.Select value={filterSheikh} onChange={e => setFilterSheikh(e.target.value)}>
                    <option value="">كل المشايخ</option>
                    {data.sheikhs.map(s => (
                      <option key={s.name} value={s.name}>{s.name}</option>
                    ))}
                  </Form.Select>
                </Col>
              )}
              <Col xs={12} md={2}>
                <Button variant="outline-secondary" onClick={load} className="w-100" disabled={loading}>
                  <i className="bi bi-arrow-clockwise ms-1" />
                  تحديث
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Error */}
        {error && (
          <Alert variant="danger" className="d-flex align-items-center gap-2">
            <i className="bi bi-exclamation-triangle-fill" />
            {error}
          </Alert>
        )}

        {/* Table */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <div className="text-muted mt-3">جارٍ تحميل البيانات...</div>
          </div>
        ) : (
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle" style={{ fontSize: '0.9rem' }}>
                  <thead className="table-light">
                    <tr>
                      <th className="d-none d-md-table-cell" style={{ width: 45 }}>#</th>
                      <th className="d-none d-md-table-cell" style={{ width: 120 }}>التاريخ</th>
                      <th>الاسم والمنصة</th>
                      <th className="d-none d-md-table-cell">الكتاب</th>
                      <th style={{ width: 140 }}>الحالة</th>
                      {!isSheikh && <th className="d-none d-md-table-cell" style={{ minWidth: 210 }}>الشيخ المعيّن</th>}
                      <th style={{ width: 90 }}>إجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={isSheikh ? 6 : 7} className="text-center py-5 text-muted">
                          <i className="bi bi-inbox fs-3 d-block mb-2" />
                          لا توجد نتائج مطابقة
                        </td>
                      </tr>
                    )}
                    {filtered.map((q, idx) => {
                      const isExpanded = expanded === q.id;
                      const waUrl = buildWhatsAppUrl(q, data.sheikhs);
                      const hasDraft = draftAnswer[q.id] !== undefined;

                      return (
                        <React.Fragment key={q.id}>
                          <tr
                            onClick={() => setExpanded(isExpanded ? null : q.id)}
                            style={{
                              cursor: 'pointer',
                              background: isExpanded ? '#f0f4ff' : undefined,
                              borderRight: isExpanded ? '3px solid #0d6efd' : '3px solid transparent',
                            }}
                          >
                            <td className="d-none d-md-table-cell text-muted">{idx + 1}</td>
                            <td className="d-none d-md-table-cell text-muted" style={{ fontSize: '0.8rem' }}>
                              {fmtDate(q.timestamp)}
                            </td>
                            <td>
                              <div className="fw-semibold">{q.name}</div>
                              <div className="d-flex flex-wrap gap-1 mt-1">
                              {q.platform && (
                                <span className="badge text-bg-light text-muted border" style={{ fontSize: '0.72rem' }}>
                                  {q.platform}
                                </span>
                              )}
                              {q.status === 'audited' && (
                                <span
                                  className="badge"
                                  style={{
                                    fontSize: '0.72rem',
                                    background: 'rgba(249,168,37,0.12)',
                                    color: '#F9A825',
                                    border: '1px solid rgba(249,168,37,0.4)',
                                  }}
                                >
                                  <i className="bi bi-patch-check-fill ms-1" />
                                  معتمد
                                </span>
                              )}
                              </div>
                              {/* Show sheikh & book inline on mobile only */}
                              {q.sheikh && (
                                <div className="d-md-none text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                                  <i className="bi bi-person-badge ms-1" />
                                  {q.sheikh}
                                </div>
                              )}
                              {q.book && (
                                <div className="d-md-none text-muted" style={{ fontSize: '0.75rem' }}>
                                  <i className="bi bi-book ms-1" />
                                  {q.book}
                                </div>
                              )}
                            </td>
                            <td className="d-none d-md-table-cell text-muted" style={{ fontSize: '0.85rem' }}>
                              {q.book || '-'}
                            </td>
                            <td onClick={e => e.stopPropagation()}>
                              {isSheikh ? (
                                (() => {
                                  const opt = STATUS_OPTIONS.find(o => o.value === (q.status || '')) || STATUS_OPTIONS[0];
                                  return (
                                    <span className={`badge text-bg-${opt.variant}`} style={{ fontSize: '0.78rem' }}>
                                      {opt.label}
                                    </span>
                                  );
                                })()
                              ) : (
                                <div className="d-flex align-items-center gap-1">
                                  <Form.Select
                                    size="sm"
                                    value={q.status || ''}
                                    onChange={e => handleStatus(q.id, e.target.value)}
                                    disabled={!canEdit || isSaving(`st_${q.id}`)}
                                    style={{ maxWidth: 130 }}
                                  >
                                    {STATUS_OPTIONS.map(o => (
                                      <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                  </Form.Select>
                                  {isSaving(`st_${q.id}`) && <Spinner size="sm" />}
                                  {result(`st_${q.id}`) === 'ok' && <i className="bi bi-check-circle-fill text-success" />}
                                  {result(`st_${q.id}`) === 'err' && <i className="bi bi-x-circle-fill text-danger" />}
                                </div>
                              )}
                            </td>
                            {!isSheikh && (
                              <td className="d-none d-md-table-cell" onClick={e => e.stopPropagation()}>
                                <div className="d-flex align-items-center gap-1">
                                  <Form.Select
                                    size="sm"
                                    value={q.sheikh || ''}
                                    onChange={e => handleSheikh(q.id, e.target.value)}
                                    disabled={!canEdit || isSaving(`sh_${q.id}`)}
                                    style={{ minWidth: 160 }}
                                  >
                                    <option value="">— اختر شيخاً —</option>
                                    {data.sheikhs.map(s => (
                                      <option key={s.name} value={s.name}>{s.name}</option>
                                    ))}
                                  </Form.Select>
                                  {isSaving(`sh_${q.id}`) && <Spinner size="sm" />}
                                  {result(`sh_${q.id}`) === 'ok' && <i className="bi bi-check-circle-fill text-success" />}
                                  {result(`sh_${q.id}`) === 'err' && <i className="bi bi-x-circle-fill text-danger" />}
                                </div>
                              </td>
                            )}
                            <td onClick={e => e.stopPropagation()}>
                              <div className="d-flex gap-1">
                                {!isSheikh && (
                                  <a
                                    href={waUrl || undefined}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`btn btn-sm ${waUrl ? 'btn-success' : 'btn-outline-secondary disabled'}`}
                                    title={waUrl ? 'إرسال عبر واتساب' : 'يرجى اختيار شيخ أولاً'}
                                    onClick={e => { if (!waUrl) e.preventDefault(); }}
                                  >
                                    <i className="bi bi-whatsapp" />
                                  </a>
                                )}
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  title={isExpanded ? 'إخفاء التفاصيل' : 'عرض التفاصيل'}
                                  onClick={() => setExpanded(isExpanded ? null : q.rowIndex)}
                                >
                                  <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
                                </Button>
                              </div>
                            </td>
                          </tr>

                          {/* Expanded detail row */}
                          {isExpanded && (
                            <tr style={{ background: '#f8f9ff' }}>
                              <td colSpan={isSheikh ? 6 : 7} className="p-3 pt-2">
                                <Row className="g-3">
                                  {/* Question + Answer editor */}
                                  <Col lg={7}>
                                    <div className="mb-3">
                                      <div className="text-secondary fw-semibold small mb-1">
                                        <i className="bi bi-question-circle ms-1" />
                                        السؤال:
                                      </div>
                                      <div
                                        style={{
                                          background: '#fff',
                                          border: '1px solid #dee2e6',
                                          borderRadius: 8,
                                          padding: '10px 14px',
                                          whiteSpace: 'pre-wrap',
                                          lineHeight: 1.9,
                                          maxHeight: 220,
                                          overflowY: 'auto',
                                          fontSize: '0.92rem',
                                        }}
                                      >
                                        {q.question}
                                      </div>
                                    </div>

                                    <div>
                                      <div className="text-secondary fw-semibold small mb-1">
                                        <i className="bi bi-chat-left-text ms-1" />
                                        الجواب:
                                      </div>
                                      <Form.Control
                                        as="textarea"
                                        rows={5}
                                        value={hasDraft ? draftAnswer[q.id] : (q.answer || '')}
                                        onChange={e => canWriteAnswer && setDraftAnswer(d => ({ ...d, [q.id]: e.target.value }))}
                                        placeholder={canWriteAnswer ? 'اكتب الجواب هنا...' : 'لا توجد صلاحية للتعديل'}
                                        readOnly={!canWriteAnswer}
                                        style={{ lineHeight: 1.9, fontSize: '0.92rem', background: canWriteAnswer ? undefined : '#f8f9fa' }}
                                      />
                                      {canWriteAnswer && (
                                        <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                                          <Button
                                            size="sm"
                                            variant="primary"
                                            disabled={!hasDraft || isSaving(`an_${q.id}`)}
                                            onClick={() => handleAnswer(q.id)}
                                          >
                                            {isSaving(`an_${q.id}`) ? (
                                              <><Spinner size="sm" className="ms-1" /> جارٍ الحفظ...</>
                                            ) : (
                                              <><i className="bi bi-floppy ms-1" /> حفظ الجواب</>
                                            )}
                                          </Button>
                                          {hasDraft && (
                                            <Button
                                              size="sm"
                                              variant="outline-secondary"
                                              onClick={() => setDraftAnswer(d => { const n = { ...d }; delete n[q.id]; return n; })}
                                            >
                                              إلغاء
                                            </Button>
                                          )}
                                          {result(`an_${q.id}`) === 'ok' && (
                                            <span className="text-success small">
                                              <i className="bi bi-check-circle-fill ms-1" /> تم الحفظ
                                            </span>
                                          )}
                                          {result(`an_${q.id}`) === 'err' && (
                                            <span className="text-danger small">
                                              <i className="bi bi-x-circle-fill ms-1" /> فشل الحفظ
                                            </span>
                                          )}
                                        </div>
                                      )}

                                      {/* Send answer to asker via WhatsApp — Admin only */}
                                      {canEdit && (() => {
                                        const answerWaUrl = buildAnswerWhatsAppUrl(q);
                                        return answerWaUrl ? (
                                          <a
                                            href={answerWaUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-success btn-sm mt-2"
                                          >
                                            <i className="bi bi-whatsapp ms-2" />
                                            إرسال الجواب للسائل عبر واتساب
                                          </a>
                                        ) : q.whatsapp && !q.answer ? (
                                          <span className="text-muted small mt-2 d-block">
                                            <i className="bi bi-whatsapp ms-1" />
                                            احفظ الجواب أولاً لإرساله للسائل
                                          </span>
                                        ) : null;
                                      })()}

                                      {/* Send answer by email via n8n — Admin only */}
                                      {isAdmin && q.email && (
                                        <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                                          {q.answer ? (
                                            <Button
                                              size="sm"
                                              variant="outline-info"
                                              disabled={!!emailSending[`email_${q.id}`]}
                                              onClick={() => handleSendEmail(q)}
                                            >
                                              {emailSending[`email_${q.id}`] ? (
                                                <><Spinner size="sm" className="ms-1" /> جارٍ الإرسال...</>
                                              ) : (
                                                <><i className="bi bi-envelope ms-1" /> إرسال الجواب بالبريد</>
                                              )}
                                            </Button>
                                          ) : (
                                            <span className="text-muted small d-block">
                                              <i className="bi bi-envelope ms-1" />
                                              احفظ الجواب أولاً لإرساله بالبريد
                                            </span>
                                          )}
                                          {emailResult[`email_${q.id}`] === 'ok' && (
                                            <span className="text-success small">
                                              <i className="bi bi-check-circle-fill ms-1" /> تم الإرسال
                                            </span>
                                          )}
                                          {emailResult[`email_${q.id}`] === 'err' && (
                                            <span className="text-danger small">
                                              <i className="bi bi-x-circle-fill ms-1" /> فشل الإرسال
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </Col>

                                  {/* Notes + Approve section — Admin and Auditor */}
                                  {(isAdmin || isAuditor) && (
                                    <Col lg={7} className="pt-0">
                                      <div
                                        style={{
                                          borderTop: '1px dashed #dee2e6',
                                          paddingTop: 14,
                                          marginTop: 4,
                                        }}
                                      >
                                        <div className="text-secondary fw-semibold small mb-2">
                                          <i className="bi bi-pencil-square ms-1" />
                                          ملاحظات المراجع
                                        </div>

                                        <Form.Control
                                          as="textarea"
                                          rows={3}
                                          value={draftANotes[q.id] !== undefined ? draftANotes[q.id] : (q.anotes || '')}
                                          onChange={e => setDraftANotes(d => ({ ...d, [q.id]: e.target.value }))}
                                          placeholder="أضف ملاحظات المراجع هنا..."
                                          style={{ lineHeight: 1.8, fontSize: '0.9rem' }}
                                        />
                                        <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                                          <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            disabled={draftANotes[q.id] == null || isSaving(`anotes_${q.id}`)}
                                            onClick={() => handleANotes(q.id)}
                                          >
                                            {isSaving(`anotes_${q.id}`) ? (
                                              <><Spinner size="sm" className="ms-1" /> جارٍ الحفظ...</>
                                            ) : (
                                              <><i className="bi bi-floppy ms-1" /> حفظ الملاحظات</>
                                            )}
                                          </Button>
                                          {draftANotes[q.id] != null && (
                                            <Button
                                              size="sm"
                                              variant="outline-secondary"
                                              onClick={() => setDraftANotes(d => { const n = { ...d }; delete n[q.id]; return n; })}
                                            >
                                              إلغاء
                                            </Button>
                                          )}
                                          {result(`anotes_${q.id}`) === 'ok' && (
                                            <span className="text-success small">
                                              <i className="bi bi-check-circle-fill ms-1" /> تم الحفظ
                                            </span>
                                          )}
                                          {result(`anotes_${q.id}`) === 'err' && (
                                            <span className="text-danger small">
                                              <i className="bi bi-x-circle-fill ms-1" /> فشل الحفظ
                                            </span>
                                          )}
                                        </div>

                                        <div className="d-flex align-items-center gap-2 mt-3 flex-wrap">
                                          {isAuditor && q.status !== 'audited' && (
                                            <Button
                                              size="sm"
                                              disabled={isSaving(`approve_${q.id}`)}
                                              style={{ background: '#F9A825', borderColor: '#F9A825', color: '#fff' }}
                                              onClick={() => handleApprove(q.id)}
                                            >
                                              {isSaving(`approve_${q.id}`) ? (
                                                <><Spinner size="sm" className="ms-1" /> جارٍ الحفظ...</>
                                              ) : (
                                                <><i className="bi bi-patch-check ms-1" /> اعتماد</>
                                              )}
                                            </Button>
                                          )}
                                          {q.status === 'audited' && (
                                            <span
                                              className="badge"
                                              style={{
                                                fontSize: '0.8rem',
                                                background: 'rgba(249,168,37,0.12)',
                                                color: '#F9A825',
                                                border: '1px solid rgba(249,168,37,0.4)',
                                                padding: '6px 10px',
                                              }}
                                            >
                                              <i className="bi bi-patch-check-fill ms-1" />
                                              معتمد
                                            </span>
                                          )}
                                          {result(`approve_${q.id}`) === 'ok' && (
                                            <span className="text-success small">
                                              <i className="bi bi-check-circle-fill ms-1" /> تم الحفظ
                                            </span>
                                          )}
                                          {result(`approve_${q.id}`) === 'err' && (
                                            <span className="text-danger small">
                                              <i className="bi bi-x-circle-fill ms-1" /> فشل الحفظ
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </Col>
                                  )}

                                  {/* Contact info + WA button */}
                                  <Col lg={5}>
                                    <Card className="border-0 bg-white shadow-sm h-100">
                                      <Card.Body className="p-3">
                                        <div className="fw-semibold text-secondary small mb-3">
                                          <i className="bi bi-person-lines-fill ms-1" />
                                          بيانات المُرسِل
                                        </div>
                                        <InfoRow icon="bi-ticket-perforated" label="رقم التذكرة" value={q.ticketNumber} />
                                        <InfoRow icon="bi-person" label="الاسم" value={q.name} />
                                        <InfoRow icon="bi-envelope" label="البريد الإلكتروني" value={q.email} />
                                        <InfoRow icon="bi-whatsapp" label="واتساب" value={q.whatsapp} />
                                        <InfoRow icon="bi-globe" label="المنصة" value={q.platform} />
                                        <InfoRow icon="bi-book" label="الكتاب" value={q.book} />

                                        {/* Sheikh selector — hidden for sheikh role */}
                                        {!isSheikh && (
                                          <div className="mb-2">
                                            <div className="text-muted" style={{ fontSize: '0.72rem' }}>الشيخ المعيّن</div>
                                            <div className="d-flex align-items-center gap-1 mt-1">
                                              <Form.Select
                                                size="sm"
                                                value={q.sheikh || ''}
                                                onChange={e => handleSheikh(q.id, e.target.value)}
                                                disabled={!canEdit || isSaving(`sh_${q.id}`)}
                                              >
                                                <option value="">— اختر شيخاً —</option>
                                                {data.sheikhs.map(s => (
                                                  <option key={s.name} value={s.name}>{s.name}</option>
                                                ))}
                                              </Form.Select>
                                              {isSaving(`sh_${q.id}`) && <Spinner size="sm" />}
                                              {result(`sh_${q.id}`) === 'ok' && <i className="bi bi-check-circle-fill text-success" />}
                                              {result(`sh_${q.id}`) === 'err' && <i className="bi bi-x-circle-fill text-danger" />}
                                            </div>
                                          </div>
                                        )}

                                        {!isSheikh && waUrl && (
                                          <a
                                            href={waUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-success btn-sm w-100 mt-3"
                                          >
                                            <i className="bi bi-whatsapp ms-2" />
                                            إرسال السؤال عبر واتساب
                                          </a>
                                        )}
                                      </Card.Body>
                                    </Card>
                                  </Col>
                                </Row>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {!loading && data.questions.length > 0 && (
                <div className="text-muted small px-3 py-2 border-top">
                  عرض {filtered.length} من {data.questions.length} سؤال
                </div>
              )}
            </Card.Body>
          </Card>
        )}
      </Container>
    </Layout>
  );
}
